import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Panel, PanelHeadline } from '@tovia/man-ui';
import { ActivityPanel } from './ActivityPanel';
import RankingDetailsPanel from 'src/client/components/panels/RankingDetailsPanel';
import { loadTags, showNewTagsModal } from 'src/client/redux/modules/tags';
import { TagsPanel } from 'src/client/components/panels/TagsPanel/TagsPanel';
import { GalleryDetailsPanel } from './GalleryDetailsPanel';
import { FULL_MODE, PPS_PURCHASED } from 'src/client/helpers/constants';
import GalleryPurchasePanel from './GalleryPurchasePanel';
import { formatDescription } from 'src/client/utils/converters';
import { useSelector, useSettingsSelector } from 'src/client/redux/modules/helpers/useSelector';
import { Gallery } from 'src/@types/Gallery';
import { PanelsGrid } from './PanelsGrid';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import moment from 'moment';
import { SFW_DATE_THRESHOLD } from 'src/shared/constants/userTypes';

type Props = {
  gallery: Gallery;
  accessMode: string;
};

export const GalleryPanels = (props: Props) => {
  const { gallery, accessMode } = props;
  const dispatch = useDispatch();

  const { config } = useSelector((state) => state.app);
  const tags = useSelector((state) => state.tags.tags);
  const showTags = useSettingsSelector('tags');
  const showDescriptionSettings = useSettingsSelector('descriptions');
  const ratingsScale = useSettingsSelector<number>('ratingsScale');
  const user = useSelector((state) => state.auth.user);
  const showSfwSite = useShowSfwSite();

  const userTags = tags[gallery.UUID] ? tags[gallery.UUID].tags : [];
  const combinedTags = [...userTags, ...gallery.uniqueTagsWithUrl];

  useEffect(() => {
    if (!user) return;

    dispatch(loadTags({ objectUUID: gallery.UUID, type: 'GALLERY' }));
  }, [user, gallery.UUID, dispatch]);

  const addNewTag = useCallback(() => {
    if (!user) return;

    dispatch(showNewTagsModal({ name: gallery.name, objectUUID: gallery.UUID, objectType: 'gallery' }));
  }, [gallery, user, dispatch]);

  const showDescriptionConfig = config.gallery.components.descriptionSection;
  const showDescriptionPanel = Boolean(
    gallery.description &&
      showDescriptionConfig &&
      showDescriptionSettings &&
      // All gallery descriptions  published after Feb 1, 2025 will be SFW
      (!showSfwSite || (showSfwSite && moment(gallery.publishedAt).isAfter(SFW_DATE_THRESHOLD))),
  );

  const hasFullAccess = accessMode === FULL_MODE || accessMode === PPS_PURCHASED;
  const showRankingPanel = hasFullAccess && config.leaderboard.active;

  return (
    <PanelsGrid data-testid="gallery-panels">
      <GalleryDetailsPanel gallery={gallery} allowDownloads={hasFullAccess} className="gallery-details" />

      <ActivityPanel
        ratingAverage={parseFloat(gallery.ratingAverage.toFixed(ratingsScale))}
        objectUUID={gallery.UUID}
        objectType={gallery.type}
        ratings={gallery.ratingCount}
        commentCount={0}
        views={gallery.views}
        itemUrl={gallery.path}
        className="gallery-activity"
        fullMode={accessMode === FULL_MODE}
      />

      {!hasFullAccess && (
        <GalleryPurchasePanel className="gallery-purchase" gallery={gallery} purchased={accessMode === PPS_PURCHASED} />
      )}

      {showRankingPanel && (
        <RankingDetailsPanel ranks={gallery.ranks} type={gallery.type} className="gallery-ranking" />
      )}

      {showDescriptionPanel && (
        <Panel className="gallery-description">
          <PanelHeadline>
            {gallery.models.map((model) => model.name).join(' & ')}
            <em> in </em>
            {gallery.name}
          </PanelHeadline>
          {formatDescription(gallery.description)}
        </Panel>
      )}

      {!!showTags && <TagsPanel tags={combinedTags} userTags={userTags} onAddNewTag={addNewTag} />}
    </PanelsGrid>
  );
};
